import React, { useContext, useMemo, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useLabels } from 'wsm-common-data';
import { setClassNames } from '@ddc/classnames-js';
import { LoadingBox } from 'wsm-ai-loader';
import useFocusTrap from '../hooks/useFocusTrap';
import InventoryTabs from './InventoryTabs';
import RecentSearches from './RecentSearches';
import shouldShowRecentSearches from '../util/shouldShowRecentSearches';
import hasEmptyResults from '../util/hasEmptyResults';
import hasResults from '../util/hasResults';
import useAriaLive from '../hooks/useAriaLive';
import isEnoughAddData from '../util/isEnoughAddData';
import { SearchContext, UIContext, ResultsContext } from '../contexts';
import InventoryStandard from './InventoryStandard';

const DropDown = () => {
	const {
		searchTerm,
		numberOfCharactersForFirstSearchNumber,
		fetchingResults,
		isNLSFetchingResults
	} = useContext(SearchContext);

	const {
		dropDownRef,
		dropDownPosition,
		inputRef,
		inputElementRef,
		inputWidth,
		modalIsOpen,
		closeModal,
		doNotRenderResults
	} = useContext(UIContext);
	const labels = useLabels();

	const {
		preparedInventoryResults,
		pageSearchResults,
		richContentToDisplay,
		recentSearches,
		handleLinkClick,
		addInfo,
		showTabs,
		hasSingleVehicle
	} = useContext(ResultsContext);

	const { suggestions, vehicles, allVehiclesPath } =
		preparedInventoryResults || {};

	const isSearchTermLongEnough = useMemo(
		() => searchTerm?.length >= numberOfCharactersForFirstSearchNumber,
		[searchTerm, numberOfCharactersForFirstSearchNumber]
	);

	const hasResultsLocal = useMemo(
		() => hasResults(pageSearchResults, suggestions, vehicles),
		[pageSearchResults, suggestions, vehicles]
	);

	const hasEmptyResultsLocal = useMemo(
		() => hasEmptyResults(pageSearchResults, suggestions, vehicles),
		[pageSearchResults, suggestions, vehicles]
	);

	const announceText = useAriaLive(
		isSearchTermLongEnough,
		hasResultsLocal,
		hasEmptyResultsLocal
	);

	const hasRichContentToDisplay = useMemo(
		() => isEnoughAddData(richContentToDisplay, addInfo),
		[richContentToDisplay, addInfo]
	);

	const shouldRenderNoResults = useMemo(
		() =>
			!hasResultsLocal &&
			hasEmptyResultsLocal &&
			!hasRichContentToDisplay &&
			!fetchingResults,
		[
			hasResultsLocal,
			hasEmptyResultsLocal,
			hasRichContentToDisplay,
			fetchingResults
		]
	);

	const viewLinkLabel = labels.get('VIEW_ALL_VEHICLES_SITE_SEARCH');
	const handleClick = useCallback(() => {
		handleLinkClick('allInventoryLink', viewLinkLabel, '');
	}, [handleLinkClick, viewLinkLabel]);

	const showNLSLoading = useMemo(
		() => !fetchingResults && isNLSFetchingResults,
		[fetchingResults, isNLSFetchingResults]
	);

	const dropDownStyle = useMemo(
		() => ({
			position: 'absolute',
			top: dropDownPosition.top,
			left: dropDownPosition.left,
			width: inputWidth,
			minHeight: '117px'
		}),
		[dropDownPosition.top, dropDownPosition.left, inputWidth]
	);

	const shouldShowRecent = useMemo(
		() =>
			shouldShowRecentSearches(
				searchTerm,
				hasResultsLocal,
				hasEmptyResultsLocal,
				recentSearches
			),
		[searchTerm, hasResultsLocal, hasEmptyResultsLocal, recentSearches]
	);

	useFocusTrap(
		[inputRef, dropDownRef],
		dropDownRef,
		null,
		inputElementRef,
		null,
		modalIsOpen,
		closeModal
	);

	const classNames = useMemo(
		() => [
			'sts-search-dropdown',
			'ddc-box-1',
			'bg-contrast-low',
			'p-4',
			!modalIsOpen && 'hidden',
			'shadow'
		],
		[modalIsOpen]
	);

	if (
		!recentSearches?.length &&
		(!searchTerm ||
			!isSearchTermLongEnough ||
			(pageSearchResults === undefined &&
				suggestions === undefined &&
				vehicles === undefined))
	) {
		return null;
	}

	const shouldRenderFlyoutContent =
		isSearchTermLongEnough && !doNotRenderResults;

	const dropDownContent = (
		<div
			ref={dropDownRef}
			className={setClassNames(classNames)}
			data-testid="sts-input-dropdown"
			data-widget-name="ws-site-text-search"
			role="dialog"
			aria-labelledby={
				searchTerm ? 'results-heading' : 'recent-searches-heading'
			}
			aria-modal="true"
			style={dropDownStyle}
		>
			<div aria-live="assertive" className={setClassNames(['sr-only'])}>
				{announceText}
			</div>

			{shouldShowRecent && <RecentSearches />}

			{showNLSLoading && (
				<LoadingBox vectorName="sparkleTwo" isGradient />
			)}

			{shouldRenderFlyoutContent && (
				<div>
					<h2
						id="results-heading"
						className={setClassNames(['sr-only'])}
					>
						{labels.get('SEARCH_RESULTS')}
					</h2>
					{showTabs ? (
						<InventoryTabs
							hasRichContentToDisplay={hasRichContentToDisplay}
							shouldRenderNoResults={shouldRenderNoResults}
						/>
					) : (
						<>
							{allVehiclesPath &&
								!shouldRenderNoResults &&
								!hasSingleVehicle && (
									<a
										onClick={handleClick}
										href={allVehiclesPath}
										className={setClassNames([
											'font-weight-bold',
											'py-3',
											'px-3'
										])}
									>
										<span>
											{viewLinkLabel}
											<i
												className={setClassNames([
													'ddc-icon',
													'ddc-icon-arrow-details',
													'ml-3'
												])}
												aria-hidden="true"
											/>
										</span>
									</a>
								)}
							<div className={setClassNames(['row'])}>
								<InventoryStandard
									vehicles={vehicles}
									suggestions={suggestions}
									shouldRenderNoResults={
										shouldRenderNoResults
									}
									hasRichContentToDisplay={
										hasRichContentToDisplay
									}
								/>
							</div>
						</>
					)}
				</div>
			)}
		</div>
	);
	return ReactDOM.createPortal(dropDownContent, document.body);
};

export default React.memo(DropDown);
